import { contractAmountForYears } from "../../common/contractYearAmount.ts";
import { g, helpers } from "../util/index.ts";
import { getClient } from "./index.ts";
import { syncNow } from "./sync.ts";

// ============================================================================
// Phase 5b: the re-sign checkpoint. After the draft, each human privately
// re-signs their own expiring players (Bird rights - allowed over the cap)
// before the blind-bid auction opens on whoever wasn't re-signed.
// ============================================================================

export type ResignCandidate = {
	league_id: string;
	pid: number;
	tid: number;
	amount: number;
	ovr: number;
	name: string;
	// Preferred expiration (season + preferred length). The `amount` above is
	// the demand at this length; other lengths cost more (contractAmountForYears)
	exp: number;
	// Vanilla's mood-based refusal: false means he won't re-sign at any price
	willing: boolean;
};

export type ResignEventPayload = {
	pid: number;
	tid: number;
	amount: number;
	exp: number;
	phase: number;
	season: number;
	name: string;
};

export const listResignCandidates = async (
	leagueId: string,
): Promise<ResignCandidate[]> => {
	const { data, error } = await getClient()
		.from("resign_candidates")
		.select()
		.eq("league_id", leagueId);
	if (error) {
		throw new Error(error.message);
	}
	// Fill in the v16.3 columns when the league's schema predates them, so an
	// un-updated database degrades to the old behaviour (everyone willing, no
	// years-based price scaling) instead of refusing every re-signing.
	return (data ?? []).map((row: any) => ({
		...row,
		willing: row.willing !== false,
		exp: typeof row.exp === "number" && row.exp > 0 ? row.exp : 0,
	})) as ResignCandidate[];
};

// Re-sign one of my own expiring players at their asking price for the chosen
// number of years. Bird rights: no cap check (re-signing your own is always
// allowed). Commits a resign event that every client applies.
export const resignPlayer = async (
	leagueId: string,
	pid: number,
	years: number,
): Promise<{ status: string }> => {
	const candidates = await listResignCandidates(leagueId);
	const candidate = candidates.find((c) => c.pid === pid);
	if (!candidate) {
		return { status: "not_a_candidate" };
	}
	// Mood-based refusal, like vanilla's accept(): no price re-signs this guy
	if (!candidate.willing) {
		return { status: "refuses" };
	}

	const boundedYears = helpers.bound(
		Math.round(years),
		g.get("minContractLength"),
		g.get("maxContractLength"),
	);
	// The player's demand rises the further the offered length is from his
	// preferred one, exactly like vanilla's negotiation options.
	const amount = contractAmountForYears({
		baseAmount: candidate.amount,
		desiredExp: candidate.exp,
		ovr: candidate.ovr,
		years: boundedYears,
		season: g.get("season"),
		minContractLength: g.get("minContractLength"),
		maxContractLength: g.get("maxContractLength"),
		minContract: g.get("minContract"),
		maxContract: g.get("maxContract"),
	});

	const event: ResignEventPayload = {
		pid,
		tid: candidate.tid,
		amount,
		exp: g.get("season") + boundedYears,
		phase: g.get("phase"),
		season: g.get("season"),
		name: candidate.name,
	};

	const { data, error } = await getClient().rpc("resign_player", {
		p_league_id: leagueId,
		p_pid: pid,
		p_event: event,
	});
	if (error) {
		throw new Error(error.message);
	}
	if (data.status !== "resigned") {
		return data;
	}

	await syncNow(leagueId);
	return { status: "resigned" };
};
